import { useEffect, useId, useState } from "react";
import { Check } from "lucide-react";
import Modal from "../../../components/Modal/Modal";
import Button from "../../../components/Button/Button";
import Textarea from "../../../components/Textarea/Textarea";

export const MAX_REMARKS_LENGTH = 500;

/**
 * The "✓ title / message / remarks / Cancel · Done" confirmation used by
 * Mark as Completed, Submit Diary and Approve Diary. The API runs only on "Done".
 * `children` renders between the message and the remarks box (e.g. a warning).
 */
const RemarksConfirmModal = ({
  isOpen,
  title,
  message,
  label = "Remarks",
  placeholder = "",
  initialValue = "",
  required = false,
  requiredMessage = "This field is required",
  isSubmitting = false,
  onClose,
  onSubmit,
  children,
}) => {
  const formId = useId();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setValue(initialValue || "");
      setError("");
    }
  }, [isOpen, initialValue]);

  const handleSubmit = (event) => {
    event.preventDefault();
    // Rendered in a portal inside other forms; don't let the submit bubble to them
    event.stopPropagation();
    if (required && !value.trim()) {
      setError(requiredMessage);
      return;
    }
    onSubmit(value.trim());
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSubmitting ? () => {} : onClose}
      size="sm"
      closable={false}
      footer={
        <div className="lp-modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" form={formId} variant="success" loading={isSubmitting}>
            Done
          </Button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit} noValidate>
        <div className="lp-confirm">
          <span className="lp-confirm__icon">
            <Check size={26} />
          </span>
          <h4>{title}</h4>
          <p>{message}</p>
        </div>

        {children}

        <Textarea
          label={label}
          name="remarks"
          id={`${formId}-remarks`}
          rows={4}
          value={value}
          maxLength={MAX_REMARKS_LENGTH}
          required={required}
          error={error}
          onChangeHandler={(event) => {
            setValue(event.target.value);
            setError("");
          }}
          placeholder={placeholder}
          autoFocus
        />
        <div className="text-end text-muted small mt-1">
          {value.length} / {MAX_REMARKS_LENGTH} characters
        </div>
      </form>
    </Modal>
  );
};

export default RemarksConfirmModal;
